import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Row, Col, Dropdown, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus, faEnvelope, faUserTag, faUsers } from '@fortawesome/free-solid-svg-icons';
import { Role } from 'src/interfaces/ManageUsersAndRole.interface';
import { useAppDispatch, useAppSelector } from 'src/redux/storeHooks';
import { addManagerAsync, createUserStaff, getRoleAsync, getRoleStaffList } from 'src/redux/actions/manageUserActions';
import useAppToast from 'src/hooks/useAppToast';
import CustomerDropdown from './CustomerDropdown';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { loggedInAdminAccessToken } from 'src/utils/constants';
import Select from 'react-select';

const AddUserModal = ({ show, handleClose, fetchUserData, userList }) => {
  const dispatch = useAppDispatch();
  const isLoginAdminAcess = !(localStorage.getItem(loggedInAdminAccessToken));
  const { fetchedUserAccountData } = useAppSelector((state) => state.userAccountData)
  const isSuperAdmin = fetchedUserAccountData?.user_type === "SUPERADMIN";
  const isSuperuser = fetchedUserAccountData?.is_superuser || fetchedUserAccountData?.account_type === 'ADMIN_STAFF';
  const [roleList, setRoleList] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const { successToast, errorToast, warningToast } = useAppToast();
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [selectedCustomers, setSelectedCustomers] = useState<
    { id: number; name: string; email: string }[]
  >([]);
  const [isAllSelect, setIsAllSelect] = useState<boolean>(false);
  const userType = fetchedUserAccountData?.user_type || '';
  const [roleSearchTerm, setRoleSearchTerm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [userData, setUserData] = useState<{
    name: string;
    email: string;
    role: string;
    customer: string[];
    is_notify: boolean;
    status: boolean;
  }>({
    name: '',
    email: '',
    role: '',
    customer: [],
    is_notify: false,
    status: true
  });

  const fetchRoleList = () => {
    setLoading(true);
    if (isSuperuser) {
      dispatch(getRoleAsync())
        .unwrap()
        .then((response) => {
          setRoleList(response?.data ?? []);
        })
        .catch((error) => {
          console.error("error Role List", error);
          errorToast(error?.data?.message || "Failed to load roles.");
        })
        .finally(() => setLoading(false));
    } else {
      dispatch(
        getRoleStaffList({
          paginationdata: { page_num: 1, page_limit: 10, is_available: true },
        })
      ).then((response) => {
        setRoleList(response?.payload?.data ?? []);
      })
        .catch((error) => {
          console.error("error Role List", error);
          errorToast(error?.data?.message || "Failed to load roles.");
        })
        .finally(() => setLoading(false));
    }
  };

  // useEffect(() => {
  //   fetchRoleList();
  // }, []);

  useEffect(() => {
    if (!show) {
      setCustomerSearchTerm('');
      setUserData({
        name: '',
        email: '',
        role: '',
        customer: [],
        is_notify: false,
        status: true
      })
      setSelectedCustomers([])
      setIsAllSelect(false)
    }
    if (show) {
      fetchRoleList();
    }
  }, [show]);

  const handleChange = (field, value) => {
    setUserData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    if (isSubmitting) return;
    const { name, email, role, is_notify, status } = userData;

    const isCustomerSelected = selectedCustomers.length > 0 || isAllSelect;
    if (isSuperuser) {
      if (!name || !email || !role || (isLoginAdminAcess && !isCustomerSelected)) {
        warningToast('Please fill all required fields.');
        return;
      }
      const payload = {
        name: name.trim(),
        email: email.trim(),
        role: roleList.find((r) => r.id === parseInt(role))?.name || "",
        role_id: parseInt(role),
        is_active: status,
        is_notify: is_notify,
        is_access_all_customer: isAllSelect
      } as any;

      if (isLoginAdminAcess) {
        setIsSubmitting(true);
        payload.customer_ids = isAllSelect ? null : selectedCustomers.join(',');
        dispatch(addManagerAsync(payload))
          .then((response) => {
            if (response.payload.status_code === 200 || response.payload.status_code === 201) {
              successToast(response.payload.massage);
              fetchUserData()
              handleClose();
            } else {
              errorToast(response?.payload?.data?.massage);
            }

          })
          .catch(() => {
            errorToast('Failed to add user.');
          })
          .finally(() => {
            setIsSubmitting(false);
          });
      } else {
        setIsSubmitting(false);
        warningToast("API not attached see logs ");
      }
    } else {
      if (!name || !email || !role) {
        warningToast('Please fill all required fields.');
        return;
      }
      setIsSubmitting(true);
      const payload = {
        email: email.trim(),
        name: name.trim(),
        user_type: userType,
        role_id: parseInt(role),
        role: roleList.find((r) => r.id === parseInt(role))?.name || "",
        is_notify: true
      }
      dispatch(createUserStaff(payload))
        .then((response) => {
          if (response.payload.status_code === 200 || response.payload.status_code === 201) {
            successToast(response.payload.massage);
                      fetchUserData()
          handleClose();
          }else{
            errorToast(response?.payload?.data?.massage);
          }
        })
        .catch(() => {
          errorToast('Failed to add user.');
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    }
  };

  return (
    <Modal show={show} onHide={isSubmitting ? undefined : handleClose} backdrop={isSubmitting ? "static" : true}
      keyboard={!isSubmitting} centered size={!isSuperuser ? "sm" : "lg"}>
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faUserPlus} className="me-2 text-success" />
          Add New User
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <style>
          {`
            .status-toggle {
              border: 1px solid #dfe3eb;
              border-radius: 0.875rem;
              padding: 0.85rem 1.1rem;
              background-color: #f8fafc;
            }

            .status-toggle__label {
              font-weight: 600;
              color: #475467;
              display: flex;
              align-items: center;
              gap: 0.35rem;
              margin-bottom: 0.35rem;
            }

            .status-toggle__state {
              font-weight: 600;
              font-size: 0.95rem;
            }

            .status-toggle__state--active {
              color: #12b76a;
            }

            .status-toggle__state--inactive {
              color: #667085;
            }

            .status-toggle__switch.form-check {
              margin: 0;
            }

            .status-toggle__switch .form-check-input {
              width: 3.25rem;
              height: 1.75rem;
              cursor: pointer;
              border: none;
            }

            .status-toggle__switch .form-check-input:checked {
              background-color: #0d6efd;
              box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.15);
            }

            .status-toggle__switch .form-check-input:not(:checked) {
              background-color: #d0d5dd;
            }

            .status-toggle__switch .form-check-label {
              display: none;
            }
          `}
        </style>
        <Form>
          <Row className="gy-4">
            <Col md={!isSuperuser ? 12 : 6}>
              <div className="d-flex flex-column gap-3 h-100">
                <div>
                  <Form.Label className="required">
                    <FontAwesomeIcon icon={faUserPlus} className="me-2 text-primary" />
                    Name
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter full name"
                    value={userData.name}
                    required
                    onChange={(e) => handleChange('name', e.target.value)}
                  />
                </div>
                <div>
                  <Form.Label className="required">
                    <FontAwesomeIcon icon={faEnvelope} className="me-2 text-danger" />
                    Email
                  </Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="user@example.com"
                    value={userData.email}
                    required
                    onChange={(e) => handleChange('email', e.target.value)}
                  />
                </div>
                <div>
                  <Form.Label className="required">
                    <FontAwesomeIcon icon={faUserTag} className="me-2 text-warning" />
                    Role
                  </Form.Label>

                  <Select
                    options={roleList?.map(role => ({ value: role.id, label: role.name }))}
                    value={
                      roleList.find(r => r.id === parseInt(userData.role))
                        ? { value: parseInt(userData.role), label: roleList.find(r => r.id === parseInt(userData.role))?.name }
                        : null
                    }
                    onChange={(selected) => handleChange('role', selected?.value.toString() || '')}
                    isSearchable
                    placeholder={loading ? 'Loading roles...' : 'Select role'}
                    isDisabled={loading}
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        borderRadius: '0.375rem', // similar to Form.Control
                        borderColor: '#dfe3eb',
                        minHeight: '38px',
                      }),
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 9999,
                      }),
                      singleValue: (provided) => ({
                        ...provided,
                        color: '#212529', // bootstrap default text color
                      }),
                    }}
                  />
                </div>

                <div className="status-toggle">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="status-toggle__label">
                        <FontAwesomeIcon icon={faUsers} className="text-info me-2" />
                        Status
                      </div>
                      <div
                        className={`status-toggle__state ${userData.status
                          ? 'status-toggle__state--active'
                          : 'status-toggle__state--inactive'
                          }`}
                      >
                        {userData.status ? 'Active' : 'Inactive'}
                      </div>
                    </div>
                    <Form.Check
                      type="switch"
                      id="statusSwitch"
                      checked={userData.status}
                      onChange={() => handleChange('status', !userData.status)}
                      label=""
                      className="status-toggle__switch"
                      aria-label="Toggle user active status"
                    />
                  </div>
                </div>
              </div>
            </Col>
            {isSuperuser && <Col md={6}>
              <div className="d-flex flex-column h-100">
                <Form.Label className="required">
                  <FontAwesomeIcon icon={faUsers} className="me-2 text-info" />
                  Customers
                </Form.Label>
                <div className="flex-grow-1 border rounded p-3 bg-light d-flex flex-column">
                  <CustomerDropdown
                    isAllSelect={isAllSelect}
                    onAllSelect={(checked) => {
                      setIsAllSelect(checked);
                    }}
                    selectedCustomers={selectedCustomers}
                    onSelect={(customerId) => {
                      setSelectedCustomers((prev) =>
                        prev.includes(customerId)
                          ? prev.filter((id) => id !== customerId)
                          : [...prev, customerId]
                      );
                    }}
                  />
                </div>
              </div>
            </Col>}
          </Row>
        </Form>
      </Modal.Body>

      <Modal.Footer className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
        {isSuperuser && <Form.Check
          type="checkbox"
          id="emailNotificationCheckbox"
          label="Receive email notifications for customer-related events"
          checked={userData.is_notify}
          onChange={() => handleChange('is_notify', !userData.is_notify)}
        />}
        <div className="ms-md-auto d-flex gap-2">
          <Button variant="outline-secondary" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleSubmit} disabled={isSubmitting} >
            {isSubmitting ? (
              <> <Spinner size="sm" animation="border" className="me-2" />
                Adding...
              </>
            ) : (
              "Add User"
            )}
          </Button>

        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default AddUserModal;
